import { 
  getWhaleBuyingActivity, 
  getWhaleSellingActivity,
  getSellingPressure, 
  getTopDelegators,
  getSilentAccumulation
} from '../delegationHistoryService';
import { formatDistanceToNow } from 'date-fns';
import { generateMarketMovementAnalysis } from '../openaiService';
import { truncateAddress } from '../../utils/chartHelpers';

/**
 * Generate a report on whale buying activity
 */
export async function getWhaleBuyingReport(hours: number = 24): Promise<string> {
  try {
    const whaleActivity = await getWhaleBuyingActivity(hours, 5000);
    
    if (whaleActivity.length === 0) {
      return `No significant whale buying activity detected in the last ${hours} hours.`;
    }
    
    // Create a more natural language report
    let report = `Whales are primarily buying `;
    
    // List the top subnets
    if (whaleActivity.length === 1) {
      report += `${whaleActivity[0].subnetName} (SN${whaleActivity[0].subnet}).`;
    } else if (whaleActivity.length === 2) {
      report += `${whaleActivity[0].subnetName} (SN${whaleActivity[0].subnet}) and ${whaleActivity[1].subnetName} (SN${whaleActivity[1].subnet}).`;
    } else {
      const topSubnets = whaleActivity.slice(0, 3);
      report += topSubnets.map((item, index) => {
        if (index === topSubnets.length - 1) {
          return `and ${item.subnetName} (SN${item.subnet})`;
        }
        return `${item.subnetName} (SN${item.subnet})`;
      }).join(', ') + '.';
    }
    
    report += '\n\n';
    
    // Add details about trading volume
    if (whaleActivity.length >= 2) {
      report += `${whaleActivity[0].subnetName} is seeing the highest buy volume at $${whaleActivity[0].totalWhaleUsd.toLocaleString()}, `;
      report += `with ${whaleActivity[1].subnetName} right behind at $${whaleActivity[1].totalWhaleUsd.toLocaleString()}.`;
      
      if (whaleActivity.length > 2) {
        report += ` ${whaleActivity[2].subnetName} has attracted $${whaleActivity[2].totalWhaleUsd.toLocaleString()} in investments over the last ${hours} hours.`;
      }
      
      report += '\n\n';
    }
    
    // Add details about significant individual transactions
    const allTransactions = whaleActivity.flatMap(item => 
      item.whaleTransactions.map(tx => ({
        ...tx,
        subnet: item.subnet,
        subnetName: item.subnetName
      }))
    );
    
    // Sort by amount (descending)
    allTransactions.sort((a, b) => b.amount - a.amount);
    
    if (allTransactions.length > 0) {
      report += 'Some significant individual transactions include ';
      
      const topTransactions = allTransactions.slice(0, 2);
      topTransactions.forEach((tx, index) => {
        const date = formatDistanceToNow(new Date(tx.timestamp), { addSuffix: true });
        const truncatedWallet = truncateAddress(tx.wallet);
        
        if (index === 0) {
          report += `a wallet (${truncatedWallet}) that bought $${tx.amount.toLocaleString()} worth of ${tx.subnetName} in a single transaction ${date}`;
        } else {
          report += `. Another investor (${truncatedWallet}) purchased $${tx.amount.toLocaleString()} in ${tx.subnetName} ${date}`;
        }
      });
      
      report += '.\n\n';
    }
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketMovementAnalysis(report);
    return analysis;
  } catch (error) {
    console.error('Error generating whale buying report:', error);
    return 'Error: Failed to generate whale buying report.';
  }
}

/**
 * Generate a report on whale selling activity
 */
export async function getWhaleSellingReport(hours: number = 24): Promise<string> {
  try {
    const whaleActivity = await getWhaleSellingActivity(hours, 5000);
    
    if (whaleActivity.length === 0) {
      return `No significant whale selling activity detected in the last ${hours} hours.`;
    }
    
    // Create a more natural language report
    let report = `Whales are primarily selling `;
    
    // List the top subnets
    if (whaleActivity.length === 1) {
      report += `${whaleActivity[0].subnetName} (SN${whaleActivity[0].subnet}).`;
    } else if (whaleActivity.length === 2) {
      report += `${whaleActivity[0].subnetName} (SN${whaleActivity[0].subnet}) and ${whaleActivity[1].subnetName} (SN${whaleActivity[1].subnet}).`;
    } else {
      const topSubnets = whaleActivity.slice(0, 3);
      report += topSubnets.map((item, index) => {
        if (index === topSubnets.length - 1) {
          return `and ${item.subnetName} (SN${item.subnet})`;
        }
        return `${item.subnetName} (SN${item.subnet})`;
      }).join(', ') + '.';
    }
    
    report += '\n\n';
    
    // Add details about sell volume
    if (whaleActivity.length >= 2) {
      report += `${whaleActivity[0].subnetName} is experiencing the highest selling pressure at $${whaleActivity[0].totalWhaleUsd.toLocaleString()}, `;
      report += `with ${whaleActivity[1].subnetName} following at $${whaleActivity[1].totalWhaleUsd.toLocaleString()}.`;
      
      if (whaleActivity.length > 2) {
        report += ` ${whaleActivity[2].subnetName} has seen $${whaleActivity[2].totalWhaleUsd.toLocaleString()} in exits over the last ${hours} hours.`;
      }
      
      report += '\n\n';
    }
    
    // Add details about significant individual transactions
    const allTransactions = whaleActivity.flatMap(item => 
      item.whaleTransactions.map(tx => ({
        ...tx,
        subnet: item.subnet,
        subnetName: item.subnetName
      }))
    );
    
    // Sort by amount (descending)
    allTransactions.sort((a, b) => b.amount - a.amount);
    
    if (allTransactions.length > 0) {
      report += 'Notable selling transactions include ';
      
      const topTransactions = allTransactions.slice(0, 2);
      topTransactions.forEach((tx, index) => {
        const date = formatDistanceToNow(new Date(tx.timestamp), { addSuffix: true });
        const truncatedWallet = truncateAddress(tx.wallet);
        
        if (index === 0) {
          report += `a wallet (${truncatedWallet}) that sold $${tx.amount.toLocaleString()} worth of ${tx.subnetName} ${date}`;
        } else {
          report += `. Another wallet (${truncatedWallet}) exited a $${tx.amount.toLocaleString()} position in ${tx.subnetName} ${date}`;
        }
      });
      
      report += '.\n\n';
    }
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketMovementAnalysis(report);
    return analysis;
  } catch (error) {
    console.error('Error generating whale selling report:', error);
    return 'Error: Failed to generate whale selling report.';
  }
}

/**
 * Generate a report on selling pressure
 */
export async function getSellingPressureReport(hours: number = 24): Promise<string> {
  try {
    const sellingPressure = await getSellingPressure(hours);
    
    if (sellingPressure.length === 0) {
      return `No significant selling activity detected in the last ${hours} hours.`;
    }
    
    // Create a more natural language report
    let report = `The following subnets are experiencing significant selling pressure over the last ${hours} hours:\n\n`;
    
    sellingPressure.slice(0, 5).forEach((item, index) => {
      const sellPercentage = (item.sellRatio * 100).toFixed(1);
      report += `${index + 1}. ${item.subnetName} (SN${item.subnet}): ${sellPercentage}% of volume is sells\n`;
      report += `   Total Volume: $${item.totalVolumeUsd.toLocaleString()}\n\n`;
    });
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketMovementAnalysis(report);
    return analysis;
  } catch (error) {
    console.error('Error generating selling pressure report:', error);
    return 'Error: Failed to generate selling pressure report.';
  }
}

/**
 * Generate a report on top active wallets
 */
export async function getTopDelegatorsReport(hours: number = 24): Promise<string> {
  try {
    const topDelegators = await getTopDelegators(hours);
    
    if (topDelegators.length === 0) {
      return `No significant delegation activity detected in the last ${hours} hours.`;
    }
    
    // Create a more natural language report
    let report = `The most active wallets in the last ${hours} hours are:\n\n`;
    
    topDelegators.slice(0, 5).forEach((delegator, index) => {
      const truncatedWallet = truncateAddress(delegator.wallet);
      report += `${index + 1}. Wallet ${truncatedWallet}\n`;
      report += `   Buy Volume: $${delegator.totalBuyUsd.toLocaleString()}\n`;
      report += `   Sell Volume: $${delegator.totalSellUsd.toLocaleString()}\n`;
      report += `   Net Flow: ${delegator.netFlowUsd >= 0 ? '+' : ''}$${delegator.netFlowUsd.toLocaleString()}\n`;
      
      // List the top 3 subnets this wallet is active in
      if (delegator.subnetsActive.length > 0) {
        const subnetNames = delegator.subnetsActive.slice(0, 3).map(id => `SN${id}`).join(', ');
        report += `   Most active in: ${subnetNames}${delegator.subnetsActive.length > 3 ? ` and ${delegator.subnetsActive.length - 3} more` : ''}\n`;
      }
      
      report += '\n';
    });
    
    // Generate analysis using OpenAI
    const analysis = await generateMarketMovementAnalysis(report);
    return analysis;
  } catch (error) {
    console.error('Error generating top delegators report:', error);
    return 'Error: Failed to generate top delegators report.';
  }
}

/**
 * Generate a report on subnets that are being silently accumulated
 * (few transactions but high net inflow)
 */
export async function getSilentAccumulationReport(hours: number = 24): Promise<string> {
  try {
    const silentAccumulation = await getSilentAccumulation(hours, 5000, 5, 20);
    
    if (silentAccumulation.length === 0) {
      // Return a varied response when no activity is detected
      const noActivityResponses = [
        "Checking on-chain data and not seeing any significant silent accumulation at the moment.",
        "After reviewing the blockchain data, I'm not detecting any notable silent accumulation patterns right now.",
        "No unusual accumulation patterns detected in the on-chain data at this time.",
        "The blockchain isn't showing any significant silent accumulation activity in the timeframe analyzed.",
        "Currently not seeing any noteworthy silent accumulation based on the on-chain data.",
        "No significant wallet activity indicating silent accumulation in the analyzed period.",
        "The on-chain data doesn't reveal any notable silent accumulation patterns at present.",
        "Based on transaction analysis, there's no significant silent accumulation happening right now.",
        "Not detecting any strategic accumulation patterns in the current on-chain data.",
        "The blockchain data isn't showing any notable silent accumulation activity at this time."
      ];
      
      // Select a random response
      const randomIndex = Math.floor(Math.random() * noActivityResponses.length);
      return noActivityResponses[randomIndex];
    }
    
    // Filter out any mentions of Sportstensor if it has no activity
    const filteredAccumulation = silentAccumulation.filter(item => {
      // Check if this is Sportstensor and if it has any transactions
      if (item.subnetName === "Sportstensor" && item.largestBuys.length === 0) {
        return false;
      }
      return true;
    });
    
    // If after filtering we have no data, return a no activity response
    if (filteredAccumulation.length === 0) {
      return "Based on transaction analysis, there's no significant silent accumulation happening right now.";
    }
    
    // Create a focused report on wallet activity and volume patterns
    const topSubnets = filteredAccumulation.slice(0, 3);
    const subnetNames = topSubnets.map(item => `${item.subnetName} (SN${item.subnet})`).join(', ');
    
    let report = `Currently watching ${subnetNames}.\n\n`;
    
    // Process each subnet to analyze wallet distribution
    for (let i = 0; i < Math.min(filteredAccumulation.length, 3); i++) {
      const item = filteredAccumulation[i];
      
      // Count unique wallets
      const uniqueWallets = new Set(item.largestBuys.map(buy => buy.wallet));
      
      // Calculate total buy volume
      const totalBuyVolume = item.largestBuys.reduce((sum, tx) => sum + tx.amount, 0);
      
      // Get transaction patterns
      const walletBuys: Record<string, {total: number, count: number, transactions: {amount: number, timestamp: string}[]}> = {};
      
      item.largestBuys.forEach(buy => {
        if (!walletBuys[buy.wallet]) {
          walletBuys[buy.wallet] = { total: 0, count: 0, transactions: [] };
        }
        walletBuys[buy.wallet].total += buy.amount;
        walletBuys[buy.wallet].count += 1;
        walletBuys[buy.wallet].transactions.push({
          amount: buy.amount,
          timestamp: buy.timestamp
        });
      });
      
      // Convert to array and sort by total amount
      const walletArray = Object.entries(walletBuys)
        .map(([wallet, data]) => ({
          wallet,
          total: data.total,
          count: data.count,
          transactions: data.transactions
        }))
        .sort((a, b) => b.total - a.total);
      
      // Add wallet distribution information
      report += `${uniqueWallets.size} wallets entered ${item.subnetName}, totaling $${totalBuyVolume.toLocaleString()} `;
      
      // Add transaction pattern information
      if (item.transactionCount > 10) {
        report += `through ${item.transactionCount} smaller transactions. `;
      } else {
        report += `with an average transaction size of $${Math.round(item.averageTransactionUsd).toLocaleString()}. `;
      }
      
      // Add volume spike information if relevant
      if (totalBuyVolume > 10000) {
        report += `This represents a significant volume spike for this subnet. `;
      }
      
      // Add information about holding patterns
      if (walletArray.length > 1) {
        const topWalletPercentage = (walletArray[0].total / totalBuyVolume) * 100;
        if (topWalletPercentage > 50) {
          report += `One wallet accounts for ${topWalletPercentage.toFixed(0)}% of all buys. `;
        } else {
          report += `Accumulation is distributed across multiple wallets with no single entity dominating. `;
        }
      }
      
      report += '\n\n';
    }
    
    // Add details about significant individual transactions
    const allTransactions = filteredAccumulation.flatMap(item => 
      item.largestBuys.map(tx => ({
        ...tx,
        subnet: item.subnet,
        subnetName: item.subnetName
      }))
    );
    
    // Sort by amount (descending)
    allTransactions.sort((a, b) => b.amount - a.amount);
    
    if (allTransactions.length > 0) {
      report += 'Some significant individual transactions include ';
      
      const topTransactions = allTransactions.slice(0, 2);
      
      // Group transactions by wallet and subnet
      const groupedTransactions: Record<string, {wallet: string, subnet: number, subnetName: string, total: number, count: number}> = {};
      
      topTransactions.forEach(tx => {
        const key = `${tx.wallet}-${tx.subnet}`;
        if (!groupedTransactions[key]) {
          groupedTransactions[key] = {
            wallet: tx.wallet,
            subnet: tx.subnet,
            subnetName: tx.subnetName,
            total: 0,
            count: 0
          };
        }
        groupedTransactions[key].total += tx.amount;
        groupedTransactions[key].count += 1;
      });
      
      const groupedArray = Object.values(groupedTransactions);
      
      groupedArray.forEach((tx, index) => {
        const truncatedWallet = truncateAddress(tx.wallet);
        
        if (index === 0) {
          if (tx.count > 1) {
            report += `a wallet (${truncatedWallet}) that bought $${tx.total.toLocaleString()} worth of ${tx.subnetName} in ${tx.count} separate transactions`;
          } else {
            report += `a wallet (${truncatedWallet}) that bought $${tx.total.toLocaleString()} worth of ${tx.subnetName} in a single transaction`;
          }
        } else {
          if (tx.count > 1) {
            report += `. Another wallet (${truncatedWallet}) invested $${tx.total.toLocaleString()} in ${tx.subnetName} across ${tx.count} transactions`;
          } else {
            report += `. Another wallet (${truncatedWallet}) invested $${tx.total.toLocaleString()} in ${tx.subnetName} in a single transaction`;
          }
        }
      });
      
      report += '.';
    }
    
    // Generate analysis using OpenAI with specific instructions
    const analysisPrompt = `
      ${report}
      
      IMPORTANT INSTRUCTIONS:
      1. Explain how multiple wallets and volume spikes are causing the AI to watch these subnets
      2. Focus on explaining how wallets are accumulating and holding
      3. Mention specific transaction patterns that indicate strategic accumulation
      4. Highlight any unusual buying patterns or volume spikes
      5. DO NOT mention "net inflow" - instead use terms like "buy volume" or "accumulation"
      6. Keep your analysis concise and focused on wallet behavior
      7. DO NOT mention Sportstensor if it has no activity
      8. DO NOT mention Nova, Nineteen, Chutes, Targon, Proprietary Trading Network, Gradients, τemplar, Celium, Apex, BitMind, Data Universe, Dojo, 404-Gen, or EfficientFrontier
    `;
    
    const analysis = await generateMarketMovementAnalysis(analysisPrompt);
    return analysis;
  } catch (error) {
    console.error('Error generating silent accumulation report:', error);
    return 'Error: Failed to generate silent accumulation report.';
  }
}